import { OverallResultExaminerData, OverallResultStatics } from '../../../../../../../store/workspace/types';
import { ChevronDownIcon, ProfileIcon } from '../../../../../../../assets/icons';
import { ForwardedRef, forwardRef, RefObject, useMemo } from 'react';
import { useExpandableRows } from '../../../../hooks/useExpandableRows';
import { useExaminerProfile } from '../../../../hooks/useExaminerProfile';
import styles from './style.module.scss';

const SUCCESS_COLOR = '#04A87C';

interface ScoreColumn {
    title: string;
    key: keyof Pick<OverallResultExaminerData,
        'grade' | 'recruitingInterviewGrade' | 'multipleChoiceExamGrade' | 'codingExamGrade'>;
    maxKey?: keyof Pick<OverallResultStatics,
        'maxRecruitingInterviewScore' | 'maxMultipleChoiceScore' | 'maxCodingScore'>;
    highlighted?: boolean;
};

const SCORE_COLUMNS: ScoreColumn[] = [
    { title: 'Overall result', key: 'grade', highlighted: true },
    { title: 'Recruiting interview result', key: 'recruitingInterviewGrade', maxKey: 'maxRecruitingInterviewScore' },
    { title: 'Multiple choice exam result', key: 'multipleChoiceExamGrade', maxKey: 'maxMultipleChoiceScore' },
    { title: 'Coding exam result', key: 'codingExamGrade', maxKey: 'maxCodingScore' },
];

interface Props {
    jobId: string | undefined;
    staticsData: OverallResultStatics | null;
};

const OverallResultsTable = forwardRef((props: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const { staticsData, jobId } = props;

    const openExaminerProfile = useExaminerProfile(jobId);
    const { isExpanded, toggleRow } = useExpandableRows<string>();
    const examiners = staticsData?.examiners;

    const maxOverallScore = useMemo(() => (staticsData?.maxRecruitingInterviewScore || 0) +
        (staticsData?.maxMultipleChoiceScore || 0) +
        (staticsData?.maxCodingScore || 0), [staticsData]);

    /** The "(Out of n)" caption; the overall column sums the individual maximums. */
    const maxScoreOf = (column: ScoreColumn) => column.highlighted
        ? (maxOverallScore !== 0 ? maxOverallScore : undefined)
        : (column.maxKey && staticsData?.[column.maxKey]) || undefined;

    return (
        <div className={styles.wrapper}>
            <div className={styles.table} ref={ref as RefObject<HTMLDivElement>}>
                <table>
                    <thead>
                        <tr data-highlight='true'>
                            <th>#</th>
                            <th>Name</th>
                            {SCORE_COLUMNS.map(column => <th key={column.key}>
                                {column.title} <br />
                                {maxScoreOf(column) && <span className={styles.desc}>
                                    (Out of {maxScoreOf(column)})
                                </span>}
                            </th>)}
                            <th>Info</th>
                        </tr>
                    </thead>
                    <tbody>
                        {examiners?.map((examiner, index) => <tr key={examiner.userId}>
                            <td>{index + 1}</td>
                            <td>{examiner.first_name} {examiner.last_name}</td>
                            {SCORE_COLUMNS.map(column => <td
                                key={column.key}
                                style={column.highlighted ? { color: SUCCESS_COLOR } : undefined}
                            >
                                {examiner[column.key] ?? 'N/A'}
                            </td>)}
                            <td data-noprint='true'>
                                <ProfileIcon onClick={() => openExaminerProfile(examiner.userId)} />
                            </td>
                        </tr>)}

                        {examiners?.length === 0 && <tr>
                            <td colSpan={7} style={{ textAlign: 'center' }}>No data.</td>
                        </tr>}
                    </tbody>
                </table>
            </div>

            <div className={styles.mobile_list}>
                {examiners?.map(examiner => <div key={examiner.userId} className={styles.card}>
                    <div className={styles.card_header} onClick={() => toggleRow(examiner.userId)}>
                        <span>{examiner.first_name} {examiner.last_name}</span>
                        <ChevronDownIcon style={{ transform: isExpanded(examiner.userId) ? 'rotate(180deg)' : 'none' }} />
                    </div>

                    {isExpanded(examiner.userId) && <div className={styles.card_body}>
                        {SCORE_COLUMNS.map(column => <div key={column.key} className={styles.row}>
                            <span className={styles.label}>{column.title}</span>
                            <span
                                className={styles.value}
                                style={column.highlighted ? { color: SUCCESS_COLOR } : undefined}
                            >
                                {examiner[column.key] ?? 'N/A'}
                            </span>
                        </div>)}
                    </div>}
                </div>)}
            </div>
        </div>
    );
});

export default OverallResultsTable;
